// src/Component/Ui_Components/Button.tsx
//
// Single source of truth for buttons across the app.
//
// Variants (fixed visual style — do not override the visual utilities via
// className; use the appropriate variant + size instead):
//   primary    — filled teal, white text                  (Save, Verify, Add, Send)
//   secondary  — white bg, gray border, dark text         (Cancel, Preview, Back)
//   ghost      — no bg, teal text                         (inline text actions e.g. "Resend")
//   danger     — filled red, white text                   (Delete, Reject)
//
// Sizes:
//   xs — px-3 py-1.5 text-xs    (in-card actions)
//   sm — px-4 py-2  text-sm     (default page action)
//   md — px-6 py-2.5 text-sm 3xl:text-base   (primary page action)
//   lg — px-6 py-3.5 text-[15px]             (auth submits, big touch targets)
//
// Extra behaviors:
//   loading  — shows a spinner, disables the click.
//   startIcon / endIcon — icon slots that layout with proper gap.
//   fullWidth — applies w-full.
//
// className is preserved for LAYOUT overrides only (mt-*, mb-*, flex-1, etc.).
// It is appended last so it wins in the DOM, but do not fight the variant's
// visual utilities from callers — pick the right variant instead.

import React from "react";

export type ButtonVariant = "primary" | "secondary" | "ghost" | "danger";
export type ButtonSize = "xs" | "sm" | "md" | "lg";

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  fullWidth?: boolean;
}

const baseStyles =
  "inline-flex items-center justify-center gap-2 font-medium transition-colors " +
  "focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0DB5B5]/40 " +
  "disabled:opacity-50 disabled:cursor-not-allowed";

const variantStyles: Record<ButtonVariant, string> = {
  primary: "bg-[#0DB5B5] text-white",
  secondary:
    "bg-white border border-[#E0E0E0] text-[#1F1F1F] hover:border-[#0DB5B5]",
  ghost: "bg-transparent text-[#0DB5B5] hover:bg-gray-50",
  danger: "bg-[#FF3B30] text-white ",
};

const sizeStyles: Record<ButtonSize, string> = {
  xs: "px-3 py-1.5 text-xs rounded-md",
  sm: "px-4 py-2 text-sm rounded-[6px]",
  md: "px-6 py-2.5 text-sm 3xl:text-base rounded-[6px]",
  lg: "px-6 py-3.5 text-[15px] rounded-lg",
};

const Spinner: React.FC = () => (
  <span
    aria-hidden="true"
    className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"
  />
);

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = "primary",
      size = "sm",
      loading = false,
      startIcon,
      endIcon,
      fullWidth = false,
      className = "",
      children,
      disabled,
      type = "button",
      ...rest
    },
    ref
  ) => {
    const classes = [
      baseStyles,
      variantStyles[variant],
      sizeStyles[size],
      fullWidth ? "w-full" : "",
      className,
    ]
      .filter(Boolean)
      .join(" ");

    return (
      <button
        ref={ref}
        type={type}
        disabled={disabled || loading}
        className={classes}
        {...rest}
      >
        {loading ? (
          <Spinner />
        ) : (
          <>
            {startIcon}
            {children}
            {endIcon}
          </>
        )}
      </button>
    );
  }
);

Button.displayName = "Button";

export default Button;
